import { Fragment, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// Plain-English labels for the queue's recommended actions.
const ACTION_LABEL = {
  "repair-address": "Fix the address (Orders tab)",
  "reconcile-printify": "Reconcile with printer",
  "review-fulfillment": "Review in Fulfillment tab",
  "retry-processing": "Resume processing",
  "retry-email": "Retry the email",
  "recheck-stock": "Recheck stock",
  "review-refund": "Review refund",
  "send-tracking": "Send tracking email",
  none: "No action — customer must repay",
};

const SEV_STYLE = {
  high: "bg-primary text-white",
  medium: "bg-accent text-black",
  info: "bg-black/10 text-black/70",
};

const RESULT_STYLE = {
  ok: "text-green-700",
  failed: "text-red-700",
  uncertain: "text-amber-700",
  already: "text-black/60",
  absent: "text-green-700",
};

const EMAIL_KIND_LABEL = {
  customer_confirmation: "Customer receipt",
  owner_alert: "Owner new-order alert",
  owner_address_alert: "Owner address alert",
  owner_fulfillment_alert: "Owner fulfillment alert",
  shipment_notification: "Tracking email",
  shipment_notification_resend: "Tracking resend",
};

const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

export const AdminRecovery = () => {
  const [queue, setQueue] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [actionResult, setActionResult] = useState(null);
  const [pauseInfo, setPauseInfo] = useState(null);
  const [pauseMsg, setPauseMsg] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [noteText, setNoteText] = useState("");

  const loadQueue = useCallback(() => {
    api.get("/admin/recovery/queue")
      .then((r) => setQueue(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Queue failed to load"));
  }, []);

  const loadPause = useCallback(() => {
    api.get("/checkout/status")
      .then((r) => { setPauseInfo(r.data); setPauseMsg(r.data.message || ""); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadQueue(); loadPause(); }, [loadQueue, loadPause]);

  const togglePause = async () => {
    const next = !pauseInfo?.paused;
    if (next && !window.confirm("Pause ALL checkout? Customers will be unable to pay until you resume.")) return;
    try {
      const r = await api.post("/admin/checkout-pause", { paused: next, message: pauseMsg });
      setPauseInfo(r.data);
      toast.success(next ? "Checkout paused — the store is browsing-only" : "Checkout resumed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change checkout state");
    }
  };

  const copyExport = async (orderId) => {
    const note = "name, street, city and ZIP removed; free-text notes/errors excluded; tracking numbers kept for the carrier";
    try {
      const r = await api.get(`/admin/recovery/${orderId}/export`);
      const text = JSON.stringify(r.data, null, 2);
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`Support export copied — ${note}`);
      } catch {
        // Clipboard blocked (permissions/headless): fall back to a file download.
        const blob = new Blob([text], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `ftr-support-export-${r.data.order?.order_number || orderId}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Clipboard blocked by the browser — downloaded the export file instead (${note})`);
      }
    } catch {
      toast.error("Export failed");
    }
  };

  const sendTracking = async (orderId, resend = false) => {
    setBusyAction("send-tracking");
    setActionResult(null);
    try {
      await api.post(`/admin/recovery/${orderId}/send-tracking`, { resend });
      setActionResult({ action: "send-tracking", result: "ok", detail: resend ? "Tracking email resent to the customer" : "Tracking email sent to the customer" });
      toast.success(resend ? "Tracking email resent" : "Tracking email sent");
      loadQueue();
      const d = await api.get(`/admin/recovery/${orderId}`);
      setDetail(d.data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Send failed";
      if (!resend && e.response?.status === 409 && /already sent/i.test(msg)) {
        setBusyAction("");
        if (window.confirm(`${msg}\n\nSend the customer a second copy?`)) {
          return sendTracking(orderId, true);
        }
        return;
      }
      setActionResult({ action: "send-tracking", result: "failed", detail: msg });
      toast.error(msg);
    } finally {
      setBusyAction("");
    }
  };

  const addNote = async (orderId) => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/admin/recovery/${orderId}/notes`, { text: noteText.trim() });
      setNoteText("");
      toast.success("Note saved");
      const d = await api.get(`/admin/recovery/${orderId}`);
      setDetail(d.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Note failed");
    }
  };

  // One operation ID per refund INTENT, persisted per order in localStorage so
  // it survives page refreshes and closing/reopening the panel — a retried
  // request after a lost response replays the SAME operation and the server
  // returns the recorded result. The ID is only discarded after a confirmed
  // success; issuing another refund afterwards is a NEW deliberate operation.
  const refundOpKey = (orderId) => `ftr_refund_op_${orderId}`;
  const refundOpIdFor = (orderId) => {
    let v = localStorage.getItem(refundOpKey(orderId));
    if (!v) {
      v = crypto.randomUUID();
      localStorage.setItem(refundOpKey(orderId), v);
    }
    return v;
  };

  const issueRefund = async (orderId) => {
    const amt = refundAmount.trim() ? parseFloat(refundAmount) : null;
    if (refundAmount.trim() && (!Number.isFinite(amt) || amt <= 0)) {
      toast.error("Enter a valid amount, or leave blank for the full remaining balance");
      return;
    }
    const label = amt ? `$${amt.toFixed(2)}` : "the full remaining balance";
    if (!window.confirm(`Really refund ${label} to the customer? This moves real money and cannot be undone. The printer order is NOT cancelled automatically.`)) return;
    setBusyAction("issue-refund");
    setActionResult(null);
    try {
      const r = await api.post(`/admin/recovery/${orderId}/issue-refund`, { amount: amt, reason: refundReason.trim(), operation_id: refundOpIdFor(orderId) });
      setActionResult({ action: "issue-refund", result: "ok", detail: `$${r.data.amount.toFixed(2)} refunded (${r.data.refund_id}) — status now ${r.data.refund_status}` });
      toast.success(`Refunded $${r.data.amount.toFixed(2)}`);
      localStorage.removeItem(refundOpKey(orderId));  // a further refund is a NEW deliberate operation
      setRefundAmount(""); setRefundReason("");
      loadQueue();
      const d = await api.get(`/admin/recovery/${orderId}`);
      setDetail(d.data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Refund failed";
      setActionResult({ action: "issue-refund", result: "failed", detail: msg });
      toast.error(msg);
    } finally {
      setBusyAction("");
    }
  };

  const openDetail = (orderId) => {
    if (openId === orderId) { setOpenId(null); setDetail(null); return; }
    setOpenId(orderId);
    setDetail(null);
    setActionResult(null);
    setRefundAmount(""); setRefundReason(""); setNoteText("");
    api.get(`/admin/recovery/${orderId}`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Could not load diagnostics"));
  };

  const runAction = async (orderId, action, body = {}) => {
    setBusyAction(action);
    setActionResult(null);
    try {
      const r = await api.post(`/admin/recovery/${orderId}/${action}`, body);
      setActionResult({ action, ...r.data });
      toast.success(`${ACTION_LABEL[action] || action}: ${r.data.result || "done"}`);
      loadQueue();
      if (openId === orderId) {
        const d = await api.get(`/admin/recovery/${orderId}`);
        setDetail(d.data);
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Action failed";
      setActionResult({ action, result: "failed", detail: msg });
      toast.error(msg);
    } finally {
      setBusyAction("");
    }
  };

  if (!queue) return <p className="font-mono text-sm text-muted-foreground">Loading the queue…</p>;

  return (
    <div data-testid="recovery-panel">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tighter">Needs attention</h2>
          <p className="font-mono text-xs text-black/50 mt-1">
            {queue.count === 0
              ? "Nothing needs you right now. This queue fills itself when something breaks."
              : `${queue.count} item(s) — every recovery action is safe: no re-charges, no duplicate printer orders.`}
          </p>
        </div>
        <button onClick={loadQueue} data-testid="recovery-refresh"
          className="inline-flex items-center gap-2 border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase hover:bg-accent transition-colors">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Store-wide checkout kill switch (v2) — visible whether or not the queue has items */}
      <div className="mb-6 border-2 border-black p-4 flex flex-wrap items-center gap-3" data-testid="checkout-pause-card">
        <div className="flex-1 min-w-[240px]">
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Store checkout</p>
          <p className="font-mono text-xs mt-1 font-bold" data-testid="checkout-pause-state">
            {!pauseInfo ? "Checking…" : pauseInfo.paused
              ? "PAUSED — customers can browse but cannot pay"
              : "Open — customers can check out normally"}
          </p>
          <input value={pauseMsg} onChange={(e) => setPauseMsg(e.target.value)}
            placeholder="Optional note shown to customers while paused"
            data-testid="checkout-pause-message" maxLength={300}
            className="mt-2 w-full border-2 border-black bg-white px-2 py-1 font-mono text-xs" />
        </div>
        <button onClick={togglePause} disabled={!pauseInfo}
          data-testid="checkout-pause-toggle"
          className={`border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase btn-press disabled:opacity-40 ${pauseInfo?.paused ? "bg-accent text-black hover:bg-black hover:text-background" : "bg-black text-background hover:bg-primary"}`}>
          {pauseInfo?.paused ? "Resume checkout" : "Pause checkout"}
        </button>
      </div>

      {queue.count === 0 ? (
        <div data-testid="recovery-empty" className="border-2 border-black bg-accent/40 p-8 text-center font-mono text-sm">
          All clear. Orders, emails, payments and the printer are all behaving.
        </div>
      ) : (
        <Table data-testid="recovery-queue-table">
          <TableHeader>
            <TableRow className="border-b-2 border-black">
              <TableHead className="font-mono text-xs uppercase">Severity</TableHead>
              <TableHead className="font-mono text-xs uppercase">Order</TableHead>
              <TableHead className="font-mono text-xs uppercase">Problem</TableHead>
              <TableHead className="font-mono text-xs uppercase">Status</TableHead>
              <TableHead className="font-mono text-xs uppercase">Last attempt</TableHead>
              <TableHead className="font-mono text-xs uppercase">Next safe action</TableHead>
              <TableHead className="font-mono text-xs uppercase"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {queue.items.map((it) => (
              <Fragment key={it.ref}>
                <TableRow data-testid={`recovery-row-${it.ref}`} className="border-b border-black/20 align-top">
                  <TableCell>
                    <span className={`px-2 py-1 font-mono text-[10px] font-bold uppercase ${SEV_STYLE[it.severity] || ""}`}
                      data-testid={`recovery-severity-${it.ref}`}>{it.severity}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div className="font-bold">{it.order_number}</div>
                    <div className="text-black/40">{it.ref}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-md">{it.problem}</TableCell>
                  <TableCell>
                    <span className={`font-mono text-[10px] font-bold uppercase ${it.status === "unknown" ? "text-amber-700" : "text-black/70"}`}
                      data-testid={`recovery-status-${it.ref}`}>
                      {it.status === "unknown" ? "Unknown — check first" : "Confirmed"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-black/60">{fmt(it.last_attempt)}</TableCell>
                  <TableCell className="font-mono text-xs font-bold">{ACTION_LABEL[it.recommended_action] || it.recommended_action}</TableCell>
                  <TableCell>
                    <button onClick={() => openDetail(it.order_id)} data-testid={`recovery-open-${it.ref}`}
                      className="border-2 border-black px-3 py-1.5 font-mono text-[11px] font-bold uppercase hover:bg-accent transition-colors">
                      {openId === it.order_id ? "Close" : "Open"}
                    </button>
                  </TableCell>
                </TableRow>
                {openId === it.order_id && (
                  <TableRow className="border-b-2 border-black">
                    <TableCell colSpan={7} className="bg-background">
                      {!detail ? (
                        <p className="font-mono text-xs text-black/50 py-4">Collecting diagnostics…</p>
                      ) : (
                        <div className="py-4 space-y-5" data-testid="recovery-detail">
                          {/* Actions */}
                          <div className="flex flex-wrap gap-3" data-testid="recovery-actions">
                            {detail.order.payment_status === "paid" && !detail.order.processing_complete && (
                              <button disabled={busyAction === "retry-processing"}
                                onClick={() => runAction(it.order_id, "retry-processing")}
                                data-testid="action-retry-processing"
                                className="border-2 border-black bg-black text-background px-4 py-2 font-mono text-xs font-bold uppercase btn-press hover:bg-primary disabled:opacity-40">
                                Resume processing (no re-charge)
                              </button>
                            )}
                            {detail.order.fulfillment_status === "submission_uncertain" && !detail.order.printify_order_id && (
                              <button disabled={busyAction === "reconcile-printify"}
                                onClick={() => runAction(it.order_id, "reconcile-printify")}
                                data-testid="action-reconcile-printify"
                                className="border-2 border-black bg-black text-background px-4 py-2 font-mono text-xs font-bold uppercase btn-press hover:bg-primary disabled:opacity-40">
                                Reconcile with printer
                              </button>
                            )}
                            {detail.emails.filter((e) => e.state === "failed").map((e) => (
                              <button key={e.kind} disabled={busyAction === "retry-email"}
                                onClick={() => runAction(it.order_id, "retry-email", { kind: e.kind })}
                                data-testid={`action-retry-email-${e.kind}`}
                                className="border-2 border-black bg-black text-background px-4 py-2 font-mono text-xs font-bold uppercase btn-press hover:bg-primary disabled:opacity-40">
                                Retry: {EMAIL_KIND_LABEL[e.kind] || e.kind}
                              </button>
                            ))}
                            <button disabled={busyAction === "recheck-stock"}
                              onClick={() => runAction(it.order_id, "recheck-stock")}
                              data-testid="action-recheck-stock"
                              className="border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase hover:bg-accent disabled:opacity-40">
                              Recheck stock (read-only)
                            </button>
                            {(detail.order.shipments || []).some((s) => s.tracking_number) && (
                              <button disabled={busyAction === "send-tracking"}
                                onClick={() => sendTracking(it.order_id)}
                                data-testid="action-send-tracking"
                                className="border-2 border-black bg-black text-background px-4 py-2 font-mono text-xs font-bold uppercase btn-press hover:bg-primary disabled:opacity-40">
                                Send tracking email
                              </button>
                            )}
                            <button onClick={() => copyExport(it.order_id)}
                              data-testid="action-copy-export"
                              className="border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase hover:bg-accent">
                              Copy support export
                            </button>
                            {["partial", "full"].includes(detail.order.refund_status) && (
                              <button disabled={busyAction === "mark-refund-reviewed"}
                                onClick={() => runAction(it.order_id, "mark-refund-reviewed")}
                                data-testid="action-mark-refund-reviewed"
                                className="border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase hover:bg-accent disabled:opacity-40">
                                Mark refund reviewed
                              </button>
                            )}
                          </div>
                          {actionResult && (
                            <div data-testid="recovery-action-result"
                              className={`border-2 border-black p-3 font-mono text-xs font-bold ${RESULT_STYLE[actionResult.result] || ""}`}>
                              {(actionResult.result || "").toUpperCase()}: {actionResult.detail || JSON.stringify(actionResult)}
                              {actionResult.results && (
                                <ul className="mt-2 font-normal text-black">
                                  {actionResult.results.map((r, i) => (
                                    <li key={i}>{r.name} — {r.color}/{r.size}: <b>{r.availability}</b>{r.note ? ` (${r.note})` : ""}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                          {/* Owner-approved refund (v2) */}
                          {detail.order.payment_status === "paid" && detail.order.refund_status !== "full" && (
                            <div className="border-2 border-black p-4" data-testid="issue-refund-form">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Issue a refund (real money)</p>
                              <p className="mt-1 font-mono text-[11px] text-black/60">
                                Refundable balance: <b className="text-black">${Math.max(0, ((detail.order.amount_paid ?? detail.order.total) || 0) - (detail.order.refunded_total || 0)).toFixed(2)}</b>
                                {" "}— refunding does NOT cancel the printer order; handle that separately in the Fulfillment tab.
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <input value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                                  placeholder="Amount $ (blank = full balance)" data-testid="refund-amount-input"
                                  className="border-2 border-black bg-white px-2 py-1.5 font-mono text-xs w-52" />
                                <input value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                                  placeholder="Reason (optional, internal note)" data-testid="refund-reason-input"
                                  className="border-2 border-black bg-white px-2 py-1.5 font-mono text-xs flex-1 min-w-[200px]" />
                                <button disabled={busyAction === "issue-refund"}
                                  onClick={() => issueRefund(it.order_id)}
                                  data-testid="action-issue-refund"
                                  className="border-2 border-black bg-primary px-4 py-2 font-mono text-xs font-bold uppercase text-white btn-press hover:bg-black disabled:opacity-40">
                                  Issue refund
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Diagnostics grid */}
                          <div className="grid gap-4 md:grid-cols-3">
                            <div className="border-2 border-black p-4" data-testid="diag-order">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Order</p>
                              <dl className="mt-2 font-mono text-xs space-y-1">
                                <div>Number: <b>{detail.order.order_number}</b></div>
                                <div>Payment: <b>{detail.order.payment_status}</b>{detail.order.livemode === false ? " (test mode)" : ""}</div>
                                <div>Fulfillment: <b>{detail.order.fulfillment_status}</b></div>
                                <div>Total paid: <b>${(detail.order.amount_paid ?? detail.order.total)?.toFixed?.(2)}</b></div>
                                <div>Placed: {fmt(detail.order.created_at)}</div>
                                <div>Paid: {fmt(detail.order.paid_at)}</div>
                                <div>Build: {detail.build}</div>
                              </dl>
                            </div>
                            <div className="border-2 border-black p-4" data-testid="diag-references">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">References</p>
                              <dl className="mt-2 font-mono text-xs space-y-1 break-all">
                                <div>Stripe session: {detail.stripe.session_id || "—"}</div>
                                <div>Printify order: {detail.order.printify_order_id || "—"}</div>
                                <div>Printer submitted: {fmt(detail.order.printify_submitted_at)}</div>
                                <div>Production approved: {fmt(detail.order.printify_approved_at)}</div>
                                {detail.order.uncertain_reason && <div className="text-amber-700">Uncertain: {detail.order.uncertain_reason}</div>}
                                {detail.order.fulfillment_error && <div className="text-red-700">Error: {detail.order.fulfillment_error}</div>}
                              </dl>
                            </div>
                            <div className="border-2 border-black p-4" data-testid="diag-emails">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Emails</p>
                              {detail.emails.length === 0 ? (
                                <p className="mt-2 font-mono text-xs text-black/50">No delivery records.</p>
                              ) : (
                                <ul className="mt-2 font-mono text-xs space-y-1">
                                  {detail.emails.map((e) => (
                                    <li key={e.kind} data-testid={`diag-email-${e.kind}`}>
                                      {EMAIL_KIND_LABEL[e.kind] || e.kind}: <b className={e.state === "sent" ? "text-green-700" : e.state === "failed" ? "text-red-700" : "text-amber-700"}>{e.state}</b>
                                      {" "}({e.attempts} attempt{e.attempts === 1 ? "" : "s"})
                                      {e.last_error ? <span className="text-red-700 block">{e.last_error}</span> : null}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                          {/* Refunds */}
                          {detail.order.refunds.length > 0 && (
                            <div className="border-2 border-black p-4" data-testid="diag-refunds">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">
                                Refunds — synced from Stripe ({detail.order.refund_status})
                              </p>
                              <ul className="mt-2 font-mono text-xs space-y-1">
                                {detail.order.refunds.map((r) => (
                                  <li key={r.refund_id}>
                                    ${r.amount.toFixed(2)} — {r.status} — {fmt(r.created)} ({r.refund_id})
                                  </li>
                                ))}
                              </ul>
                              {detail.order.refund_pending_total > 0 && (
                                <p className="mt-1 font-mono text-[11px] text-amber-700">
                                  Plus ${detail.order.refund_pending_total.toFixed(2)} still pending at Stripe — not yet money moved.
                                </p>
                              )}
                              <p className="mt-2 font-mono text-[11px] text-black/60">
                                A Stripe refund does NOT cancel the printer order — handle the printer side separately in the Fulfillment tab.
                              </p>
                            </div>
                          )}
                          {/* Stock check */}
                          {detail.order.stock_check && (
                            <div className="border-2 border-black p-4" data-testid="diag-stock">
                              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">
                                Last stock check — {fmt(detail.order.stock_check.at)} by {detail.order.stock_check.by}
                              </p>
                              <ul className="mt-2 font-mono text-xs space-y-1">
                                {detail.order.stock_check.results.map((r, i) => (
                                  <li key={i}>{r.name} — {r.color}/{r.size}: <b>{r.availability}</b>{r.note ? ` (${r.note})` : ""}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/* Notes (v2) */}
                          <div className="border-2 border-black p-4" data-testid="diag-notes">
                            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Notes</p>
                            {(detail.order.notes || []).length === 0 ? (
                              <p className="mt-2 font-mono text-xs text-black/50">No notes yet — jot down what support said, what you promised the customer, anything.</p>
                            ) : (
                              <ul className="mt-2 font-mono text-xs space-y-1" data-testid="notes-list">
                                {detail.order.notes.map((n, i) => (
                                  <li key={i}>{fmt(n.at)} — {n.text} <span className="text-black/40">({n.by})</span></li>
                                ))}
                              </ul>
                            )}
                            <div className="mt-2 flex gap-2">
                              <input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") addNote(it.order_id); }}
                                placeholder="Add a note…" maxLength={1000} data-testid="note-input"
                                className="border-2 border-black bg-white px-2 py-1.5 font-mono text-xs flex-1" />
                              <button onClick={() => addNote(it.order_id)}
                                data-testid="note-add-button"
                                className="border-2 border-black px-4 py-1.5 font-mono text-xs font-bold uppercase hover:bg-accent">
                                Add note
                              </button>
                            </div>
                          </div>
                          {/* Action history */}
                          <div className="border-2 border-black p-4" data-testid="diag-history">
                            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary">Action history</p>
                            {detail.action_history.length === 0 ? (
                              <p className="mt-2 font-mono text-xs text-black/50">No recovery actions yet.</p>
                            ) : (
                              <ul className="mt-2 font-mono text-xs space-y-1">
                                {detail.action_history.map((a, i) => (
                                  <li key={i}>
                                    {fmt(a.at)} — {a.action} — <b className={RESULT_STYLE[a.result] || ""}>{a.result}</b>
                                    {a.detail ? ` — ${a.detail}` : ""} (by {a.actor})
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
